import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { forkJoin, Observable, Subject, of } from 'rxjs';
import { catchError, defaultIfEmpty, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import dayjs from '../../config/dayjs.config';
import { ProductService } from '../../service/product.service';
import { Ticket, TicketGroup } from '../../model/product';
import { captureSurfacedFailure } from '../../util/sentry-capture';
import { SwallowedHttpError, failIfSwallowed } from '../../util/swallowed-http';
import {
  BlockedTicket,
  GateModel,
  HolidayDate,
  OffScheduleGroup,
  buildGateModel,
  countSteps,
  hasBlockedWork,
} from './review-ticket-gate.logic';

/**
 * 검수 요청이 "예약 가능한 일정 없음"으로 막힌 호스트를 한 화면에 한 가지씩 물어 통과시킨다.
 *
 * 켜기와 검수 요청을 한 동작으로 묶지 않는다 — 호스트가 바뀐 결과를 보고 직접 검수를 누른다.
 */
const KST = 'Asia/Seoul';

type GatePhase = 'loading' | 'error' | 'empty' | 'stuck' | 'flow';
type GateStep = 'pick' | 'reopen' | 'done';

interface Selectable<T> {
  value: T;
  selected: boolean;
}

@Component({
  selector: 'app-review-ticket-gate',
  templateUrl: './review-ticket-gate.component.html',
  styleUrls: ['./review-ticket-gate.component.scss'],
})
export class ReviewTicketGateComponent implements OnChanges, OnDestroy {
  @Input() productId: number | null = null;
  /** 서버의 오픈 게이트와 같은 판정 결과. true면 막힐 이유가 없어 아무것도 묻지 않는다. */
  @Input() hasReservableTicket = true;
  /** 예약 마감 시간(시). 이 안에 든 회차는 켜도 예약을 받지 못해 "켜면 된다"고 말할 수 없다. */
  @Input() reservationHours = 0;
  /** 게이트가 상태를 바꿨을 때. 호출부가 서버 판정을 다시 조회한다. */
  @Output() reservableChange = new EventEmitter<void>();

  phase: GatePhase = 'loading';
  step: GateStep = 'pick';
  groups: Selectable<OffScheduleGroup>[] = [];
  tickets: Selectable<BlockedTicket>[] = [];
  holidays: Selectable<HolidayDate>[] = [];
  hasPastSchedule = false;
  totalSteps = 1;
  submitting = false;
  errorMessage = '';

  /**
   * 흐름에 한 번 들어가면 서버 판정이 바뀌어도 되감지 않는다. 켠 직후 판정을 다시 읽으면 아직
   * 막힌 회차가 남아 false 가 돌아오고, 그때 다시 조회하면 방금 켠 그룹이 목록에서 빠져 호스트가
   * "일정을 먼저 등록해주세요"로 되돌아간다 — 이 PR 이 없애려던 바로 그 지시다.
   */
  private started = false;
  /** 분류 시점에 1단계 대상이 있었는지. 단계 번호 표시의 기준이다. */
  private hadOffGroups = false;
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly productService: ProductService) {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.productId && !changes.hasReservableTicket) return;
    // 완료 콜백의 가드만으로는 부족하다 — 앞 상품의 전송이 끝나지 않은 채 상품이 바뀌면
    // `submitting` 이 켜진 상태로 남아 새 상품의 버튼이 "바꾸는 중…"으로 잠긴다.
    if (changes.productId) {
      this.started = false;
      this.submitting = false;
      this.errorMessage = '';
    }
    if (this.started) return;
    if (this.hasReservableTicket || !this.productId) return;
    this.load();
  }

  /** 안내를 띄울 상태인지. 흐름 중에는 서버 판정과 무관하게 띄운다. */
  get visible(): boolean {
    return !!this.productId && (!this.hasReservableTicket || this.started);
  }

  get stepNumber(): number {
    if (this.step === 'pick') return 1;
    // 1단계가 있었는지는 분류 시점에 굳힌 값으로 판단한다 — 성공분을 목록에서 빼면 `groups` 가
    // 비어 2단계에서 다시 "1단계"를 그린다.
    if (this.step === 'reopen') return this.hadOffGroups ? 2 : 1;
    return this.totalSteps;
  }

  get selectedGroups(): OffScheduleGroup[] {
    return this.groups.filter((item) => item.selected).map((item) => item.value);
  }

  get selectedTickets(): BlockedTicket[] {
    return this.tickets.filter((item) => item.selected).map((item) => item.value);
  }

  get selectedHolidays(): HolidayDate[] {
    return this.holidays.filter((item) => item.selected).map((item) => item.value);
  }

  get hasReopenSelection(): boolean {
    return this.selectedTickets.length > 0 || this.selectedHolidays.length > 0;
  }

  /**
   * 라벨은 모두 KST 고정이다. `dayjs.config` 는 기본 타임존을 세우지 않으므로 포맷을 그냥 쓰면
   * 브라우저 로케일을 따라, 같은 화면에서 휴무 행(KST)과 회차 행이 하루 어긋난다.
   */
  formatDay(value: Date | string): string {
    return dayjs(value).tz(KST).format('M[월] D[일]');
  }

  formatDayTime(value: Date | string): string {
    return dayjs(value).tz(KST).format('M[월] D[일] HH:mm');
  }

  formatKstDay(kstDate: string): string {
    return dayjs.tz(kstDate, KST).format('M[월] D[일]');
  }

  /**
   * 비동기로 바뀌는 **단계 번호**만 읽힌다. 제목까지 넣으면 같은 문장이 두 번 낭독된다.
   * 초기 렌더로 끝나는 안내(로딩·실패·등록 필요)에는 아무것도 내보내지 않는다.
   */
  get announcement(): string {
    return this.phase === 'flow' ? `${this.totalSteps}단계 중 ${this.stepNumber}단계` : '';
  }

  private load() {
    const productId = this.productId;
    if (!productId) return;
    this.phase = 'loading';
    this.errorMessage = '';

    forkJoin({
      groups: this.query(this.productService.getTicketGroups(productId, false), 'load-groups', { productId }),
      // 보조 정보라 실패해도 전체를 막지 않는다 — 이게 실패하면 "지나간 일정" 한 줄만 빠진다.
      pastGroups: this.query(this.productService.getTicketGroups(productId, true), 'load-past-groups', {
        productId,
      }).pipe(catchError(() => of([] as TicketGroup[]))),
    })
      .pipe(
        switchMap(({ groups, pastGroups }) => {
          this.hasPastSchedule = pastGroups.length > 0;
          if (groups.length === 0) return of({ groups, tickets: [] as Ticket[] });
          return this.fetchGroupTickets(productId).pipe(map((tickets) => ({ groups, tickets })));
        }),
        // 보고는 `query()` 가 이미 단계별로 했다 — 여기서 또 캡처하면 한 장애가 두 건으로 집계된다.
        catchError(() => of(null)),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        // 상품을 바꿔 다시 조회한 뒤 앞 상품의 응답이 늦게 오면, 그 목록으로 다른 상품의 일정을
        // 켜게 된다. 호출부(`meeting-edit.page.ts`)가 쓰는 것과 같은 가드다.
        if (this.productId !== productId) return;
        // 조회 실패는 빈 결과와 다르다 — 섞으면 일정이 있는 호스트에게 "등록하러 가라"고 말한다.
        if (!result) {
          this.phase = 'error';
          return;
        }
        this.applyModel(buildGateModel(result.groups, result.tickets, this.reservationHours, new Date()));
      });
  }

  private applyModel(model: GateModel) {
    this.groups = model.offGroups.map((value) => ({ value, selected: true }));
    this.tickets = model.blockedTickets.map((value) => ({ value, selected: true }));
    this.holidays = model.holidayDates.map((value) => ({ value, selected: true }));
    this.totalSteps = countSteps(model);
    this.hadOffGroups = model.offGroups.length > 0;

    if (!model.hasFutureTicket) {
      this.phase = 'empty';
      return;
    }
    // 켜거나 열 것이 하나도 없다 — 남은 회차가 모두 예약 마감 시간을 지났다는 뜻이라 회차를 더해야 한다.
    if (model.offGroups.length === 0 && !hasBlockedWork(model)) {
      this.phase = 'stuck';
      return;
    }
    this.phase = 'flow';
    this.started = true;
    // 판매중인데 전부 마감·휴무인 상품은 켤 일정이 없으므로 1단계를 건너뛴다.
    this.step = model.offGroups.length > 0 ? 'pick' : 'reopen';
  }

  /**
   * 이 상품의 **미래 회차 전체**를 조회한다. 그룹 기간(`from`/`to`)으로 좁히지 않는다 —
   * 「무제한」 연장 그룹은 `to` 가 null 이고(`UNLIMITED_EXTENSION_MONTHS`), 회차는 6개월치가
   * 생성되므로 그룹 기간에서 범위를 만들면 그 회차가 전부 빠져 "일정을 먼저 등록해주세요"가 된다.
   * 미래로 좁히는 일은 `isOld: false` 가 서버에서 이미 한다(`date > UTC_TIMESTAMP()`).
   */
  private fetchGroupTickets(productId: number): Observable<Ticket[]> {
    return this.query(this.productService.searchTickets({ productId: [productId], isOld: false }), 'load-tickets', {
      productId,
    });
  }

  activateSelected() {
    const targets = this.selectedGroups;
    const productId = this.productId;
    if (this.submitting || targets.length === 0 || !productId) return;
    this.submitting = true;
    this.errorMessage = '';

    forkJoin(
      targets.map((group) =>
        this.mutate(this.productService.activateTicketGroup(group.id, true), 'activate-group', {
          productId,
          groupId: group.id,
        }),
      ),
    ).subscribe((results) => {
      // 앞 상품의 전송이 늦게 끝나면 그 결과로 지금 보고 있는 상품의 단계를 넘기고
      // `reservableChange` 까지 올린다. 조회 쪽과 같은 가드다.
      if (this.productId !== productId) return;
      this.submitting = false;
      const failed = results.filter((ok) => !ok).length;
      // 일부만 성공해도 그 그룹은 이미 켜졌다 — 성공분을 목록에서 빼지 않으면 호스트가 다시 보낸다.
      const activated = new Set(targets.filter((_, index) => results[index]).map((group) => group.id));
      this.groups = this.groups.filter((item) => !activated.has(item.value.id));
      // 하나도 못 바꿨으면 "바뀌었다"를 올리지 않는다 — 부모가 헛되게 판정을 다시 읽는다.
      if (failed < results.length) this.reservableChange.emit();
      // 하나라도 실패하면 1단계에 머문다 — 넘어가 버리면 남은 일정을 다시 시도할 화면이 없다.
      if (failed > 0) {
        this.errorMessage =
          failed === results.length
            ? '판매 상태를 바꾸지 못했습니다. 잠시 후 다시 시도해주세요.'
            : `${results.length}건 중 ${failed}건을 바꾸지 못했습니다. 남은 일정을 다시 시도해주세요.`;
        return;
      }
      this.step = this.hasBlockedWork ? 'reopen' : 'done';
    });
  }

  reopenSelected() {
    const tickets = this.selectedTickets;
    const holidays = this.selectedHolidays;
    const productId = this.productId;
    if (this.submitting || (tickets.length === 0 && holidays.length === 0) || !productId) return;
    this.submitting = true;
    this.errorMessage = '';

    const requests: Observable<boolean>[] = [
      ...tickets.map((ticket) =>
        this.mutate(this.productService.setReservationEndTicket(ticket.id, false), 'reopen-ticket', {
          productId,
          ticketId: ticket.id,
        }),
      ),
      ...holidays.map((holiday) =>
        this.mutate(this.productService.setCloseTicket(productId, new Date(holiday.sample), false), 'reopen-holiday', {
          productId,
          kstDate: holiday.kstDate,
        }),
      ),
    ];

    forkJoin(requests).subscribe((results) => {
      if (this.productId !== productId) return;
      this.submitting = false;
      const failed = results.filter((ok) => !ok).length;
      // 성공분을 남겨 두면 "남은 회차"가 무엇인지 화면이 가리키지 못하고 재시도가 전건을 다시 보낸다.
      const reopenedTickets = new Set(tickets.filter((_, index) => results[index]).map((ticket) => ticket.id));
      const reopenedHolidays = new Set(
        holidays.filter((_, index) => results[tickets.length + index]).map((holiday) => holiday.kstDate),
      );
      this.tickets = this.tickets.filter((item) => !reopenedTickets.has(item.value.id));
      this.holidays = this.holidays.filter((item) => !reopenedHolidays.has(item.value.kstDate));
      if (failed < results.length) this.reservableChange.emit();
      // 하나라도 실패하면 이 단계에 머문다 — 남은 회차를 다시 시도할 화면이 필요하다.
      if (failed > 0) {
        this.errorMessage =
          failed === results.length
            ? '예약을 다시 열지 못했습니다. 잠시 후 다시 시도해주세요.'
            : `${results.length}건 중 ${failed}건을 열지 못했습니다. 남은 회차를 다시 시도해주세요.`;
        return;
      }
      this.step = 'done';
    });
  }

  /** 아직 열어야 할 것이 남았는지. 2단계로 넘길지 끝낼지를 가른다. */
  private get hasBlockedWork(): boolean {
    return this.tickets.length > 0 || this.holidays.length > 0;
  }

  /**
   * 조회용. 502 는 인터셉터가 `EMPTY` 로 삼켜 `next` 도 `error` 도 돌지 않으므로, 되살리지 않으면
   * 화면이 "확인하고 있습니다…"에서 멈춘다.
   */
  private query<T>(source: Observable<T>, phase: string, extra: Record<string, unknown>): Observable<T> {
    return source.pipe(
      failIfSwallowed(),
      catchError((error) => {
        captureSurfacedFailure(error, { feature: 'host-review-ticket-gate', phase, extra });
        throw error;
      }),
    );
  }

  /**
   * 변경용. 건별로 성공/실패를 돌려줘 부분 실패를 "아무것도 안 바뀌었다"로 보고하지 않는다.
   * 삼켜진 502 는 값 없이 complete 하므로 `defaultIfEmpty` 가 실패로 받는다 — 본문 없는 응답까지
   * 에러로 바꾸는 `failIfSwallowed` 를 뮤테이션에 쓰지 않는 이유다.
   */
  private mutate(source: Observable<unknown>, phase: string, extra: Record<string, unknown>): Observable<boolean> {
    return source.pipe(
      map(() => true),
      defaultIfEmpty(false),
      // 삼켜진 502 는 에러 없이 complete 하므로 `catchError` 를 거치지 않는다 — 여기서 보고하지
      // 않으면 호스트는 실패 문구를 보는데 운영에는 아무 기록이 남지 않는다.
      tap((ok) => {
        if (!ok) {
          captureSurfacedFailure(new SwallowedHttpError(), {
            feature: 'host-review-ticket-gate',
            phase,
            extra,
          });
        }
      }),
      catchError((error) => {
        captureSurfacedFailure(error, { feature: 'host-review-ticket-gate', phase, extra });
        return of(false);
      }),
    );
  }
}
